import { useState } from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { snakeCase } from "change-case";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Modal from "modules/Modal/Modal";
import { CreateCustomContentTypeInput } from "generated/graphql";
import useCreateOrganizationCustomContentType from "hooks/organization/useCreateOrganizationCustomContentType";

interface AddProposalTermModalProps {
  open: boolean;
  onClose: () => void;
}

const schema = z.object({
  contentType: z.string().min(1, "Term name is required"),
  showOnSimple: z.boolean(),
  showOnFull: z.boolean(),
  showOnBranded: z.boolean(),
  showOnTabular: z.boolean(),
});

type FormValues = CreateCustomContentTypeInput;

export default function AddProposalTermModal({ open, onClose }: AddProposalTermModalProps) {
  const [createCustomContentType, { loading }] = useCreateOrganizationCustomContentType();

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      contentType: "",
      showOnSimple: false,
      showOnFull: false,
      showOnBranded: false,
      showOnTabular: false,
    },
  });

  const onSubmit = (values: FormValues) => {
    const contentType = snakeCase(values.contentType);

    createCustomContentType({
      variables: {
        input: {
          ...values,
          contentType,
        },
      },
      onCompleted: () => {
        methods.reset();
        onClose();
      },
    });
  };

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} styles={{ width: "500px", height: "auto" }}>
      <MDBox p={2}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          Add Proposal Term
        </MDTypography>

        <MDBox component="form" role="form" onSubmit={methods.handleSubmit(onSubmit)}>
          <MDBox mb={2}>
            <Controller
              name="contentType"
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Term Name"
                  fullWidth
                  required
                  error={!!error}
                  helperText={error?.message}
                  autoFocus
                />
              )}
            />
          </MDBox>

          <MDBox mb={2}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                <MDTypography variant="body2" fontWeight="medium" mb={0.5}>
                  Show on Layouts
                </MDTypography>
              </FormLabel>
              <FormGroup>
                <Controller
                  name="showOnSimple"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Simple"
                    />
                  )}
                />
                <Controller
                  name="showOnFull"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Full"
                    />
                  )}
                />
                <Controller
                  name="showOnBranded"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Branded"
                    />
                  )}
                />
                <Controller
                  name="showOnTabular"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Tabular"
                    />
                  )}
                />
              </FormGroup>
            </FormControl>
          </MDBox>

          <MDBox display="flex" justifyContent="flex-end" gap={1}>
            <MDButton variant="outlined" color="secondary" onClick={handleClose}>
              Cancel
            </MDButton>
            <MDButton variant="gradient" color="success" type="submit" disabled={loading}>
              Create
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}
